/**
 * LikeCommunityMessageUseCase
 *
 * Ports the optimistic community-message like flow:
 *   - `CommunityMessageWidget._onLikePress` (the like-record BUILD),
 *     lib/presentation/custom_widgets/communit_message_widget.dart:120-171.
 *   - `ContentReactionsRepository.likeCommunityMessage` (the optimistic
 *     local-storage write, server call, and revert-on-failure),
 *     lib/data/repository/content_reactions_repository.dart:87-111.
 *
 * `execute` receives the ALREADY-computed target `liked` boolean (the caller
 * passes `!isLiked`, where `isLiked` comes from the same watched storage record —
 * the web equivalent of Flutter's `liked = !_messageLike!.liked`,
 * communit_message_widget.dart:143). It:
 *   1. reads the previous local like-record from SecureStorage (the record the
 *      Task-4 hook also watches);
 *   2. builds the new `CommunityMessageLikeDto` per Flutter's two branches:
 *      - FIRST like (no prior record, communit_message_widget.dart:132-140):
 *        `communityMessageId = message.id`, `parentCommunityMessageId =
 *        message.parentId`, `createdDate = now`, `userId = current uid`, and
 *        **no `updatedDate`**.
 *      - TOGGLE (record exists, communit_message_widget.dart:141-152): preserve
 *        `communityMessageId` / `parentCommunityMessageId` / `createdDate` /
 *        `userId` from the previous record and stamp `updatedDate = now`.
 *      (This deliberately differs from the sibling `likeCommentUseCase`, which
 *      ALWAYS stamps `updatedDate`; community-Flutter stamps it only on toggle —
 *      parity wins.)
 *   (A) optimistically writes the new record under
 *       `SECURE_STORAGE_KEYS.COMMUNITY_MESSAGE_LIKES + messageId` (the same key
 *       the login sync populates, syncSecureStorageUseCase.ts:281). This write is
 *       what fires the Task-4 hook's storage listener to re-tint the heart — the
 *       web port of Flutter's Hive box watch (communit_message_widget.dart:86-88,
 *       109-118; same architecture note as saveRoadmapItemLikeUseCase.ts:36-45).
 *   (B) calls `CommunityService.likeMessage(dto)` (repository -> data-source hop,
 *       content_reactions_repository.dart:98);
 *   (C) on `!success`, reverts the optimistic write (restore the previous
 *       record, or remove the key when there was none — the established web
 *       revert pattern, likeCommentUseCase.ts:51-57). This diverges from
 *       Flutter's revert (content_reactions_repository.dart:99-109, post-Task-6
 *       fix), which REBUILDS the record: it sets `liked = currentLikeState.liked`
 *       (the pre-action flag) but keeps the FAILED attempt's `createdDate` /
 *       `updatedDate`, so after a failed FIRST like it leaves a `liked=false`
 *       record in the box rather than no record. The web instead restores the
 *       EXACT previous record / removes the key, so a retry after a failed first
 *       like takes the FIRST-like branch and sends a fresh `createdDate`
 *       (`updatedDate` null) instead of the failed attempt's dates. The
 *       business-truth `liked` flag — the only field the UI reads
 *       (useCommunityMessageLike.ts: `data?.liked ?? false`) — is identical in
 *       every case; this timestamp-visible difference on the failed-first-like
 *       retry path is deliberate, precedent-consistent, and inert for `liked`.
 *
 * Flutter revert bug (fixed on web here; fixed in Flutter in Task 6):
 *   `content_reactions_repository.dart:99-108` rebuilds the pre-action record but
 *   writes it under the SIBLING `CommentLike.keyName` (:107) instead of
 *   `CommunityMessageLike.keyName`, and never restores `parentCommunityMessageId`
 *   (:100-105) — an obvious copy-paste bug from the `likeComment` method above it
 *   (:60-85). The revert therefore lands on a key nobody reads/watches. Per
 *   conventions.md "Handling obvious Flutter bugs" we do NOT port the bug: the
 *   web reverts under the correct key (restore-previous-or-remove), so the net
 *   `liked` flag matches Flutter's intent. Commit footer:
 *   `Bug found on Flutter and fixed in both clients.`
 *
 * `HapticFeedback.lightImpact()` (communit_message_widget.dart:157-159) has no
 * web equivalent and is intentionally not ported (precedent:
 * saveRoadmapItemLikeUseCase.ts:46-47). The likes-count optimistic update, the
 * own-message / in-flight guards, and the failure toast
 * (communit_message_widget.dart:121-127, 154-155, 163-168) are PRESENTATION
 * concerns handled in Task 5, not here.
 */

import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import { CommunityMessageLikeRemapper } from '@/domain/remappers/communityMessageLike/communityMessageLikeRemapper';
import { CommunityService } from '@/domain/services/community/communityService';
import type { CommunityMessageLikeDto } from '@/data/dtos/communityMessageLike/communityMessageLikeDto';
import type { CommunityMessageLikeStorage } from '@/domain/entities/secureStorage/secureStorageEntities';

export interface LikeCommunityMessageParams {
  /** `widget.message.id` — the liked message's id (also the storage-key suffix). */
  messageId: string;
  /**
   * `widget.message.parentId` — the parent (wall) message id, `undefined` for
   * top-level wall messages. Only used to seed a FIRST like's
   * `parentCommunityMessageId`; a toggle preserves the previous record's value.
   */
  messageParentId?: string;
  /** `AppAuthenticationService.getCurrentFirebaseUser()!.uid` — the liker. */
  currentUserId: string;
  /**
   * The NEW desired like state. The caller passes `!isLiked`, the web equivalent
   * of Flutter's `liked = !_messageLike!.liked` (communit_message_widget.dart:143),
   * where `isLiked` comes from the same watched storage record. A FIRST like
   * therefore arrives as `true`.
   */
  liked: boolean;
}

export class LikeCommunityMessageUseCase {
  constructor(
    private service = CommunityService.getInstance(),
    private storage = SecureStorageWebCrypto.getInstance(),
  ) {}

  async execute(params: LikeCommunityMessageParams): Promise<boolean> {
    const storageKey = SECURE_STORAGE_KEYS.COMMUNITY_MESSAGE_LIKES + params.messageId;
    const previous = await this.storage.getEncrypted<CommunityMessageLikeStorage>(storageKey);

    // Build the new like record per Flutter's two branches
    // (communit_message_widget.dart:132-152). A FIRST like (no previous record)
    // gets a fresh `createdDate` and NO `updatedDate`; a toggle preserves
    // `createdDate` / `parentCommunityMessageId` and stamps `updatedDate = now`.
    const now = Date.now();
    const dto: CommunityMessageLikeDto = {
      // Toggle preserves the previous record's userId
      // (communit_message_widget.dart:150); a FIRST like stamps the current uid
      // (:139-140). Identical values in every reachable state (records are the
      // current user's own), kept strictly two-branch for parity, like
      // `parentCommunityMessageId` below. (`communityMessageId` needs no branch:
      // `previous.communityMessageId` equals `params.messageId` by construction —
      // the record is stored under `COMMUNITY_MESSAGE_LIKES + params.messageId`.)
      userId: previous ? previous.userId : params.currentUserId,
      communityMessageId: params.messageId,
      parentCommunityMessageId: previous
        ? previous.parentCommunityMessageId
        : params.messageParentId,
      liked: params.liked,
      createdDate: previous?.createdDate ?? now,
      updatedDate: previous ? now : undefined,
    };

    // (A) Optimistic local write BEFORE the server call — this fires the Task-4
    // hook's storage listener to re-tint the heart (content_reactions_repository.dart:93-96).
    await this.storage.setEncrypted(storageKey, CommunityMessageLikeRemapper.toStorage(dto));

    // (B) Server call (content_reactions_repository.dart:98).
    const success = await this.service.likeMessage(dto);

    // (C) Revert on failure (content_reactions_repository.dart:99-108, corrected
    // key): restore the previous record when it existed, else remove the key.
    if (!success) {
      if (previous) {
        await this.storage.setEncrypted(storageKey, previous);
      } else {
        await this.storage.remove(storageKey);
      }
    }

    return success;
  }
}

export const likeCommunityMessageUseCase = new LikeCommunityMessageUseCase();
